import { useService } from '@aesop-fables/containr-react';
import { useObservable } from '@aesop-fables/scrinium';
import { Stack, Button } from '@mui/material';
import { useContext } from 'react';
import { ToastbarContext } from '../../../App';
import { Svgs } from '../../../assets/svg';
import { Spacer } from '../../../components';
import { ButtonMenu } from '../../../components/ButtonMenu';
import {
  GenerateOrgRevShareCsv,
  GenerateTeamRevShareCsv,
  GenerateGroupRevShareCsv,
  GenerateOrgChargesCsv,
  GenerateTeamChargesCsv,
  GenerateGroupChargesCsv,
} from '../../../data/rev-share/commands';
import { useCommands } from '../../../helpers/useCommands';
import { Toast } from '../../../models/Toast';
import {
  DrillDownWizard,
  drillDownWizardKey,
  AdminTierTypeEnum,
} from '../../../services/drillDown';
import type { AxiosResponse } from 'axios';

interface ExportButtonsProps {
  start: string;
  end: string;
  onExportPdf: () => Promise<void>;
}

function getRevShareCommand(tier: AdminTierTypeEnum) {
  switch (tier) {
    case AdminTierTypeEnum.Root:
      return GenerateOrgRevShareCsv;
    case AdminTierTypeEnum.Organization:
      return GenerateTeamRevShareCsv;
    case AdminTierTypeEnum.Team:
      return GenerateGroupRevShareCsv;
    default:
      throw new Error(`Unsupported admin tier for RevShare: ${tier}`);
  }
}

function getChargesCommand(tier: AdminTierTypeEnum) {
  switch (tier) {
    case AdminTierTypeEnum.Root:
      return GenerateOrgChargesCsv;
    case AdminTierTypeEnum.Organization:
      return GenerateTeamChargesCsv;
    case AdminTierTypeEnum.Team:
      return GenerateGroupChargesCsv;
    default:
      throw new Error(`Unsupported admin tier for Charges: ${tier}`);
  }
}

const ExportButtons: React.FC<ExportButtonsProps> = ({ start, end, onExportPdf }) => {
  const { setToast } = useContext(ToastbarContext);
  const commands = useCommands();
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const tier = useObservable(drillDownWizard.tier$);

  const downloadFile = (response: AxiosResponse, filename: string) => {
    const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async (type: 'revshare' | 'charges' | 'both') => {
    if (!tier || !start || !end) {
      console.error('Export failed, missing required data:', { tier, start, end });
      return;
    }

    const range = { startDate: start, endDate: end };

    try {
      if (type === 'revshare' || type === 'both') {
        const revShareCommand = getRevShareCommand(tier);
        const res = await commands.execute(revShareCommand, range);
        const filename = `transactions-${start}-${end}.csv`;
        downloadFile(res, filename);
      }

      if (type === 'charges' || type === 'both') {
        const chargesCommand = getChargesCommand(tier);
        const res = await commands.execute(chargesCommand, range);
        const filename = `charges-${start}-${end}.csv`;
        downloadFile(res, filename);
      }
    } catch (error) {
      console.error('Export failed:', error);
      setToast(
        new Toast({
          message: 'Failed to generate transaction report',
          severity: 'error',
          open: true,
          autoHideDuration: 5000,
        }),
      );
    }
  };

  const exportCsvItems = [
    { label: 'Export Share Only', onClick: () => handleExportCsv('revshare') },
    { label: 'Export Charges Only', onClick: () => handleExportCsv('charges') },
    { label: 'Export Both Separately', onClick: () => handleExportCsv('both') },
  ];

  return (
    <Stack direction='row' spacing={2} justifyContent='flex-end' alignItems='stretch'>
      <ButtonMenu
        items={exportCsvItems}
        color='secondary'
        sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
        <Stack direction='row' alignItems='center'>
          <Svgs.IconExport style={{ flexShrink: 0 }} />
          <Spacer width='xxxs' />
          Export CSV
        </Stack>
      </ButtonMenu>

      <Button
        color='secondary'
        sx={{ fontSize: '12px', flex: 1, whiteSpace: 'nowrap' }}
        onClick={onExportPdf}>
        <Svgs.IconExport style={{ flexShrink: 0 }} />
        <Spacer width='xxxs' />
        Export PDF
      </Button>
    </Stack>
  );
};

export default ExportButtons;
